import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useScheduleActionLock } from '../../hook/useScheduleActionLock';
import dayjs from 'dayjs';
import { AlertCircle, Bell, CalendarDays, CheckCircle2, Info, ListChecks, Loader2, Search, Users } from 'lucide-react';
import Modal from '../modal/Modal';
import { Col, Row } from '../../lib/utils';
import { MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import {
    useBulkReserveMemberTicketSessions,
    useMemberTicketBookableSessions,
} from '../../features/groupTicket/hooks';
import {
    BulkReserveMemberTicketSessionsResponse,
    MemberTicketAccess,
    MemberTicketBookableSession,
} from '../../features/groupTicket/types';
import { MembershipPauseState } from '../../features/membershipPause/types';
import { isPassInUse } from '../../features/membershipPause/status';
import useMembershipPauseCreateGate from '../MembershipPause/useMembershipPauseCreateGate';
import MembershipPauseBadge from '../MembershipPause/MembershipPauseBadge';

type GroupTicketMembershipLike = {
    _id?: string;
    name?: string;
    ticketName?: string;
    ticketId?: string | { name?: string };
    expirationDate?: string;
    expiryDate?: string;
    remainSession?: number;
    remainingCount?: number;
    /** 수강권 일시정지(목록 행·호환 상세 모두 실린다). 생성 확인 창 판정에만 쓴다. */
    pause?: MembershipPauseState | null;
    status?: string;
    isDeleted?: boolean;
    isCanceled?: boolean;
    /** 공유받은 행이면 SHARED + 소유자 — 「풀고 등록」이 소유자 수강권 정지를 푼다는 안내에 쓴다 */
    access?: MemberTicketAccess;
};

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    centerId: string;
    memberId: string;
    memberName?: string;
    membership: GroupTicketMembershipLike;
    createAlert: any;
};

type StatusFilter = 'all' | 'available' | 'selected' | 'blocked' | 'booked';

const statusTone: Record<string, { label: string; bg: string; color: string; border: string }> = {
    BOOKABLE: { label: '예약 가능', bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
    WAITLIST_AVAILABLE: { label: '대기 가능', bg: '#FFFBEB', color: '#92400E', border: '#FDE68A' },
    ALREADY_RESERVED: { label: '예약됨', bg: '#F8FAFC', color: '#475569', border: '#CBD5E1' },
    ALREADY_WAITLISTED: { label: '대기중', bg: '#F8FAFC', color: '#475569', border: '#CBD5E1' },
    ALREADY_USED: { label: '기록 있음', bg: '#F8FAFC', color: '#475569', border: '#CBD5E1' },
    FULL: { label: '마감', bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
    PERIOD_MISMATCH: { label: '기간 밖', bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
    NO_REMAINING_COUNT: { label: '회차 부족', bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
    TICKET_NOT_ALLOWED: { label: '사용 불가', bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
    TICKET_INACTIVE: { label: '비활성', bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
    CANCELLED: { label: '취소됨', bg: '#F8FAFC', color: '#64748B', border: '#CBD5E1' },
    PAST_SESSION: { label: '시간 지남', bg: '#F8FAFC', color: '#64748B', border: '#CBD5E1' },
};

const weekdayLabel = ['일', '월', '화', '수', '목', '금', '토'];

const resolveTicketName = (membership: GroupTicketMembershipLike) => {
    const populatedTicket = membership?.ticketId;
    if (populatedTicket && typeof populatedTicket === 'object' && 'name' in populatedTicket) {
        return (populatedTicket as any).name as string;
    }
    return membership?.name || membership?.ticketName || '그룹 수강권';
};

const getMembershipExpiryDate = (membership: GroupTicketMembershipLike) =>
    membership?.expirationDate || membership?.expiryDate;

const getMembershipRemainingCount = (membership: GroupTicketMembershipLike) =>
    membership?.remainSession ?? membership?.remainingCount ?? 0;

const getDefaultEndDate = (membership: GroupTicketMembershipLike) => {
    const fallback = dayjs().add(8, 'week');
    const expiryDate = getMembershipExpiryDate(membership);
    const expiry = expiryDate ? dayjs(expiryDate) : fallback;
    if (!expiry.isValid() || expiry.isBefore(dayjs(), 'day')) return fallback.format('YYYY-MM-DD');
    return (expiry.isBefore(fallback, 'day') ? expiry : fallback).format('YYYY-MM-DD');
};

const isSelectableSession = (session: MemberTicketBookableSession) =>
    session.status === 'BOOKABLE' || session.status === 'WAITLIST_AVAILABLE';

const ModalGroupTicketSessionRegister = ({
    setIsVisible,
    centerId,
    memberId,
    memberName,
    membership,
    createAlert,
}: Props) => {
    const memberTicketId = membership?._id || '';
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(getDefaultEndDate(membership));
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('available');
    const [notifyMember, setNotifyMember] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [result, setResult] = useState<BulkReserveMemberTicketSessionsResponse | null>(null);

    const params = useMemo(() => {
        const next: Record<string, string> = { startDate, endDate };
        if (search.trim()) next.search = search.trim();
        // 공유 참여자를 예약할 때는 '이미 예약됨' 판정도 그 참여자 기준이어야 한다.
        if (memberId) next.memberId = memberId;
        return next;
    }, [startDate, endDate, search, memberId]);

    const { data, isLoading, isFetching, refetch } = useMemberTicketBookableSessions({
        centerId,
        memberTicketId,
        params,
        enabled: !!centerId && !!memberTicketId,
    });
    const bulkReserveMutation = useBulkReserveMemberTicketSessions();

    const summary = data?.summary;
    const sessions = data?.results ?? [];
    const remainingCount = summary?.remainingCount ?? getMembershipRemainingCount(membership);
    const ticketName = summary?.ticketName || resolveTicketName(membership);

    const filteredSessions = useMemo(() => {
        return sessions.filter((session) => {
            if (statusFilter === 'available') return isSelectableSession(session);
            if (statusFilter === 'selected') return selectedIds.has(session.sessionId);
            if (statusFilter === 'booked') {
                return ['ALREADY_RESERVED', 'ALREADY_WAITLISTED', 'ALREADY_USED'].includes(session.status);
            }
            if (statusFilter === 'blocked') return !isSelectableSession(session);
            return true;
        });
    }, [sessions, selectedIds, statusFilter]);

    const selectableIds = useMemo(
        () => filteredSessions.filter((session) => isSelectableSession(session)).map((session) => session.sessionId),
        [filteredSessions]
    );

    const selectedCount = selectedIds.size;
    const selectedSessions = useMemo(
        () => sessions.filter((session) => selectedIds.has(session.sessionId)),
        [selectedIds, sessions]
    );
    const selectedReserveCount = selectedSessions.filter((session) => session.status === 'BOOKABLE').length;
    const selectedWaitlistCount = selectedSessions.filter((session) => session.status === 'WAITLIST_AVAILABLE').length;
    const canSubmit = selectedCount > 0 && selectedCount <= remainingCount && !bulkReserveMutation.isLoading;
    const selectedOverRemain = selectedCount > remainingCount;
    const availableCount = (summary?.bookable ?? 0) + (summary?.waitlistAvailable ?? 0);
    const failedResults = result?.results?.filter((item) => item.status === 'FAILED') ?? [];
    const notificationPreview = notifyMember
        ? selectedCount === 0
            ? '등록 성공 건수 기준으로 회원에게 알림을 발송합니다.'
            : selectedCount > 1
            ? '2개 이상은 회원에게 요약 푸시 1건으로 안내됩니다.'
            : '1개 등록 시 기존 예약/대기 알림으로 안내됩니다.'
        : '회원 알림은 발송하지 않습니다.';
    const hasMoreSessions = summary?.hasMore === true;

    const setQuickRange = (days: number) => {
        setStartDate(dayjs().format('YYYY-MM-DD'));
        setEndDate(dayjs().add(days, 'day').format('YYYY-MM-DD'));
        clearSelection();
    };

    const toggleSession = (session: MemberTicketBookableSession) => {
        if (!isSelectableSession(session)) return;
        setResult(null);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(session.sessionId)) {
                next.delete(session.sessionId);
                return next;
            }
            if (next.size >= remainingCount) return next;
            next.add(session.sessionId);
            return next;
        });
    };

    const selectVisible = () => {
        setResult(null);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            for (const id of selectableIds) {
                if (next.size >= remainingCount) break;
                next.add(id);
            }
            return next;
        });
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
        setResult(null);
    };

    const pauseGate = useMembershipPauseCreateGate({ createAlert });
    /* 스케줄 동작 잠금(설계 §6): 여러 회차 한 번에 예약 = 기존 수업 회원 구성 변경 = 수정 */
    const editLock = useScheduleActionLock('schedule.edit');

    const handleSubmit = async () => {
        if (!canSubmit) return;
        // 스케줄 동작 잠금 재검사 — 정지 해제(첫 부수 쓰기)보다 **먼저** 본다(설계 §6-6)
        if (editLock.requiresCheck() && !(await editLock.ensure())) return;
        // 정지 기간 안 수업이면 「풀고 등록 / 그대로 등록 / 취소」(설계서 P8). 대기 등록도 생성으로 본다.
        const proceed = await pauseGate.confirmBeforeCreate([
            {
                ref: { kind: 'group', centerId, memberTicketId },
                pause: membership?.pause,
                inUse: isPassInUse(membership),
                memberName: memberName || '회원',
                passName: resolveTicketName(membership),
                startTimes: selectedSessions.map((session) => session.startTime),
                expiry: getMembershipExpiryDate(membership) || null,
                isShared: membership?.access?.type === 'SHARED',
                sharedOwnerName: membership?.access?.type === 'SHARED' ? membership.access.ownerName || null : null,
            },
        ]);
        if (!proceed) return;
        try {
            const res = await bulkReserveMutation.mutateAsync({
                centerId,
                memberTicketId,
                memberId,
                body: {
                    sessionIds: Array.from(selectedIds),
                    allowWaitlist: false,
                    waitlistSessionIds: selectedSessions
                        .filter((session) => session.status === 'WAITLIST_AVAILABLE')
                        .map((session) => session.sessionId),
                    skipExisting: true,
                    suppressMemberNotify: !notifyMember,
                },
            });
            const body = res?.data ?? null;
            setResult(body);
            setSelectedIds(new Set());
            await refetch();
            const doneMessage = `예약 ${body?.reserved ?? 0}개, 대기 ${body?.waitlisted ?? 0}개가 등록되었습니다.\n${body?.notification?.message || (notifyMember ? '회원 알림 발송을 요청했습니다.' : '회원 알림은 발송하지 않았습니다.')}${body?.failed ? `\n실패 ${body.failed}건은 결과에서 확인해주세요.` : ''}`;
            createAlert(
                '수업 등록 완료',
                // 「풀고 등록」 뒤 개별 실패가 있으면 정지가 풀린 사실을 앞에 붙인다(검증 4). 건수 표시는 그대로.
                body?.failed ? pauseGate.decorateFailureMessage(doneMessage) : doneMessage,
                false
            );
        } catch (err: any) {
            createAlert(
                '수업 등록 실패',
                pauseGate.decorateFailureMessage(err?.serverMessage || err?.message || '수업 등록 중 오류가 발생했습니다.'),
                true,
            );
        }
    };

    const renderStatusBadge = (session: MemberTicketBookableSession) => {
        const tone = statusTone[session.status] || { label: session.status, bg: '#F8FAFC', color: '#475569', border: '#CBD5E1' };
        return (
            <span
                style={{
                    minWidth: 78,
                    textAlign: 'center',
                    padding: '5px 10px',
                    borderRadius: 999,
                    border: `1px solid ${tone.border}`,
                    backgroundColor: tone.bg,
                    color: tone.color,
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: '16px',
                }}
            >
                {tone.label}
            </span>
        );
    };

    return (
        <Modal
            setIsVisible={setIsVisible}
            whiteViewWidth={920}
            whiteViewHeight={720}
            isCloseButton
            mt={40}
            mb={40}
            backgroundOpacity={0.35}
        >
            <Col className="h-full bg-[#F8FAFC]">
                <Col className="border-b border-solid border-[#E5E7EB] bg-white px-[32px] py-[24px]">
                    <Row className="items-start justify-between gap-[24px] pr-[44px]">
                        <Col>
                            <MediumText fontSize={22} lineHeight={30} letterSpacing={0} color="#111827">
                                그룹수업 등록
                            </MediumText>
                            <RegularText fontSize={14} lineHeight={21} letterSpacing={0} color="#6B7280" className="mt-[6px]">
                                {memberName || '회원'} · {ticketName}
                            </RegularText>
                            {/* 수강권 일시정지 배지 — 이 창은 수강권 하나를 다루므로 목록 대신 머리에 단다 */}
                            <MembershipPauseBadge pass={membership} className="mt-[8px] self-start" />
                        </Col>
                        <Row className="gap-[10px]">
                            <SummaryPill label="잔여" value={`${remainingCount}회`} tone="blue" />
                            <SummaryPill label="예약" value={`${selectedReserveCount}개`} tone={selectedOverRemain ? 'red' : 'green'} />
                            <SummaryPill label="대기" value={`${selectedWaitlistCount}개`} tone="amber" />
                            <SummaryPill label="가능" value={`${availableCount}개`} tone="green" />
                        </Row>
                    </Row>
                </Col>

                <Col className="gap-[16px] px-[32px] py-[20px]">
                    <Row className="items-center gap-[10px]">
                        <FieldWithIcon icon={<CalendarDays size={16} color="#64748B" />}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(event) => {
                                    setStartDate(event.target.value);
                                    clearSelection();
                                }}
                                style={fieldInputStyle}
                            />
                        </FieldWithIcon>
                        <RegularText fontSize={13} lineHeight={18} color="#94A3B8">부터</RegularText>
                        <FieldWithIcon icon={<CalendarDays size={16} color="#64748B" />}>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(event) => {
                                    setEndDate(event.target.value);
                                    clearSelection();
                                }}
                                style={fieldInputStyle}
                            />
                        </FieldWithIcon>
                        <FieldWithIcon icon={<Search size={16} color="#64748B" />} className="flex-1">
                            <input
                                value={search}
                                onChange={(event) => {
                                    setSearch(event.target.value);
                                    clearSelection();
                                }}
                                placeholder="수업명 검색"
                                style={fieldInputStyle}
                            />
                        </FieldWithIcon>
                        <button
                            type="button"
                            onClick={() => refetch()}
                            style={ghostButtonStyle}
                        >
                            조회
                        </button>
                    </Row>

                    <Row className="items-center gap-[8px]">
                        <RegularText fontSize={12} lineHeight={17} color="#64748B">빠른 기간</RegularText>
                        <RangeButton label="2주" onClick={() => setQuickRange(14)} />
                        <RangeButton label="4주" onClick={() => setQuickRange(28)} />
                        <RangeButton label="8주" onClick={() => setQuickRange(56)} />
                        <RangeButton
                            label="수강권 기간"
                            onClick={() => {
                                setStartDate(dayjs().format('YYYY-MM-DD'));
                                setEndDate(getDefaultEndDate(membership));
                                clearSelection();
                            }}
                        />
                    </Row>

                    <Row className="items-center justify-between gap-[12px]">
                        <Row className="gap-[8px]">
                            <FilterButton active={statusFilter === 'available'} onClick={() => setStatusFilter('available')} label="예약/대기 가능" />
                            <FilterButton active={statusFilter === 'selected'} onClick={() => setStatusFilter('selected')} label={`선택됨 ${selectedCount}`} />
                            <FilterButton active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label="전체" />
                            <FilterButton active={statusFilter === 'booked'} onClick={() => setStatusFilter('booked')} label="등록됨" />
                            <FilterButton active={statusFilter === 'blocked'} onClick={() => setStatusFilter('blocked')} label="불가" />
                        </Row>
                        <Row className="items-center gap-[12px]">
                            <label className="flex cursor-pointer items-center gap-[7px]">
                                <input
                                    type="checkbox"
                                    checked={notifyMember}
                                    onChange={(event) => setNotifyMember(event.target.checked)}
                                    style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#2563EB' }}
                                />
                                <RegularText fontSize={13} lineHeight={18} color="#475569">회원 알림 발송</RegularText>
                            </label>
                            <button type="button" onClick={selectVisible} style={ghostButtonStyle}>
                                보이는 수업 선택
                            </button>
                            <button type="button" onClick={clearSelection} style={ghostButtonStyle}>
                                선택 해제
                            </button>
                        </Row>
                    </Row>

                    {(hasMoreSessions || selectedWaitlistCount > 0 || notifyMember) && (
                        <Col className="gap-[6px]">
                            {hasMoreSessions && (
                                <NoticeLine
                                    tone="amber"
                                    icon={<Info size={14} />}
                                    text={`조건에 맞는 수업이 ${summary?.limit ?? 200}개를 넘어 먼저 시작하는 수업만 표시했습니다.`}
                                />
                            )}
                            {selectedWaitlistCount > 0 && (
                                <NoticeLine
                                    tone="amber"
                                    icon={<ListChecks size={14} />}
                                    text="대기는 확정될 때 회차가 차감됩니다. 과예약 방지를 위해 잔여 회차 안에서 선택합니다."
                                />
                            )}
                            {notifyMember && (
                                <NoticeLine
                                    tone="blue"
                                    icon={<Bell size={14} />}
                                    text={notificationPreview}
                                />
                            )}
                        </Col>
                    )}
                </Col>

                <Col className="mx-[32px] min-h-0 flex-1 overflow-hidden rounded-[8px] border border-solid border-[#E5E7EB] bg-white">
                    <Row className="border-b border-solid border-[#E5E7EB] bg-[#F8FAFC] px-[18px] py-[11px]">
                        <HeaderCell width={44} text="" />
                        <HeaderCell width={106} text="날짜" />
                        <HeaderCell width={104} text="시간" />
                        <HeaderCell flex text="수업" />
                        <HeaderCell width={118} text="정원" />
                        <HeaderCell width={110} text="상태" />
                    </Row>
                    <Col className="min-h-0 flex-1 overflow-y-auto">
                        {(isLoading || isFetching) && (
                            <Col className="h-[280px] items-center justify-center gap-[10px]">
                                <Loader2 size={24} color="#2563EB" className="animate-spin" />
                                <RegularText fontSize={14} lineHeight={20} color="#64748B">수업을 불러오는 중입니다</RegularText>
                            </Col>
                        )}
                        {!isLoading && !isFetching && filteredSessions.length === 0 && (
                            <Col className="h-[280px] items-center justify-center gap-[8px]">
                                <AlertCircle size={24} color="#94A3B8" />
                                <RegularText fontSize={15} lineHeight={22} color="#475569">
                                    {statusFilter === 'selected' ? '선택한 수업이 없습니다' : '조건에 맞는 수업이 없습니다'}
                                </RegularText>
                            </Col>
                        )}
                        {!isLoading && !isFetching && filteredSessions.map((session) => {
                            const selected = selectedIds.has(session.sessionId);
                            const selectable = isSelectableSession(session);
                            const date = dayjs(session.startTime);
                            return (
                                <Row
                                    key={session.sessionId}
                                    className="items-center border-b border-solid border-[#F1F5F9] px-[18px] py-[13px]"
                                    style={{
                                        cursor: selectable ? 'pointer' : 'default',
                                        backgroundColor: selected
                                            ? session.status === 'WAITLIST_AVAILABLE' ? '#FFFBEB' : '#EFF6FF'
                                            : '#FFFFFF',
                                        opacity: selectable ? 1 : 0.74,
                                    }}
                                    onClick={() => toggleSession(session)}
                                >
                                    <div style={{ width: 44, display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            disabled={!selectable}
                                            onChange={() => toggleSession(session)}
                                            onClick={(event) => event.stopPropagation()}
                                        />
                                    </div>
                                    <Col style={{ width: 106 }}>
                                        <MediumText fontSize={13} lineHeight={18} color="#111827">
                                            {date.format('MM.DD')}
                                        </MediumText>
                                        <RegularText fontSize={12} lineHeight={17} color="#64748B">
                                            {weekdayLabel[date.day()]}요일
                                        </RegularText>
                                    </Col>
                                    <Col style={{ width: 104 }}>
                                        <MediumText fontSize={13} lineHeight={18} color="#111827">
                                            {date.format('HH:mm')}
                                        </MediumText>
                                        <RegularText fontSize={12} lineHeight={17} color="#64748B">
                                            {dayjs(session.endTime).format('HH:mm')}
                                        </RegularText>
                                    </Col>
                                    <Col className="min-w-0 flex-1 pr-[14px]">
                                        <MediumText fontSize={14} lineHeight={20} color="#111827" className="truncate">
                                            {session.title}
                                        </MediumText>
                                        <RegularText fontSize={12} lineHeight={17} color="#64748B" className="truncate">
                                            {session.coachName || '강사 미배정'}{session.roomId ? ` · ${session.roomId}` : ''}
                                            {!selectable ? ` · ${session.reason}` : ''}
                                        </RegularText>
                                    </Col>
                                    <Row style={{ width: 118 }} className="items-center gap-[6px]">
                                        <Users size={14} color="#64748B" />
                                        <RegularText fontSize={13} lineHeight={18} color="#334155">
                                            {session.currentCount}/{session.maxCapacity}
                                        </RegularText>
                                        {session.waitlistLimit > 0 && (
                                            <RegularText fontSize={12} lineHeight={17} color="#64748B">
                                                대기 {session.waitlistCount}/{session.waitlistLimit}
                                            </RegularText>
                                        )}
                                    </Row>
                                    <div style={{ width: 110, display: 'flex', justifyContent: 'flex-end' }}>
                                        {renderStatusBadge(session)}
                                    </div>
                                </Row>
                            );
                        })}
                    </Col>
                </Col>

                {result && (
                    <Row className="mx-[32px] mt-[14px] items-start gap-[12px] rounded-[8px] border border-solid border-[#D1FAE5] bg-[#ECFDF5] px-[16px] py-[12px]">
                        <CheckCircle2 size={18} color="#047857" />
                        <Col className="flex-1">
                            <MediumText fontSize={14} lineHeight={20} color="#065F46">
                                예약 {result.reserved}개 · 대기 {result.waitlisted}개 · 건너뜀 {result.skipped}개 · 실패 {result.failed}개
                            </MediumText>
                            {result.notification?.message && (
                                <RegularText fontSize={12} lineHeight={18} color="#047857" className="mt-[3px]">
                                    {result.notification.message}
                                </RegularText>
                            )}
                            {result.failed > 0 && (
                                <Col className="mt-[6px] gap-[3px]">
                                    {failedResults.slice(0, 3).map((item) => (
                                        <RegularText key={item.sessionId} fontSize={12} lineHeight={18} color="#047857">
                                            {item.title || '수업'} · {item.reason}
                                        </RegularText>
                                    ))}
                                    {failedResults.length > 3 && (
                                        <RegularText fontSize={12} lineHeight={18} color="#047857">
                                            외 {failedResults.length - 3}건
                                        </RegularText>
                                    )}
                                </Col>
                            )}
                        </Col>
                    </Row>
                )}

                <Row className="mt-auto items-center justify-between border-t border-solid border-[#E5E7EB] bg-white px-[32px] py-[18px]">
                    <RegularText fontSize={13} lineHeight={18} color={selectedOverRemain ? '#B91C1C' : '#64748B'}>
                        {selectedOverRemain
                            ? '선택한 수업이 잔여 회차보다 많습니다.'
                            : `예약 ${selectedReserveCount}개 · 대기 ${selectedWaitlistCount}개를 등록합니다. ${notificationPreview}`}
                    </RegularText>
                    <Row className="gap-[10px]">
                        <button type="button" onClick={() => setIsVisible(false)} style={cancelButtonStyle}>
                            닫기
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            style={{
                                ...primaryButtonStyle,
                                backgroundColor: canSubmit ? '#2563EB' : '#CBD5E1',
                                cursor: canSubmit ? 'pointer' : 'not-allowed',
                            }}
                        >
                            {bulkReserveMutation.isLoading
                                ? '등록 중...'
                                : selectedWaitlistCount > 0
                                    ? `예약 ${selectedReserveCount}개 · 대기 ${selectedWaitlistCount}개 등록`
                                    : `선택한 ${selectedCount}개 등록`}
                        </button>
                    </Row>
                </Row>
            </Col>
            {pauseGate.modal}
            {editLock.gate}
        </Modal>
    );
};

const SummaryPill = ({ label, value, tone }: { label: string; value: string; tone: 'blue' | 'green' | 'red' | 'amber' }) => {
    const palette = {
        blue: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
        green: { bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
        red: { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
        amber: { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A' },
    }[tone];
    return (
        <Col
            className="min-w-[82px] items-center rounded-[8px] border border-solid px-[12px] py-[8px]"
            style={{ backgroundColor: palette.bg, borderColor: palette.border }}
        >
            <RegularText fontSize={11} lineHeight={15} color="#64748B">{label}</RegularText>
            <MediumText fontSize={16} lineHeight={22} color={palette.color}>{value}</MediumText>
        </Col>
    );
};

const FieldWithIcon = ({ icon, children, className = '' }: { icon: React.ReactNode; children: React.ReactNode; className?: string }) => (
    <Row className={`h-[40px] items-center gap-[8px] rounded-[8px] border border-solid border-[#E2E8F0] bg-white px-[10px] ${className}`}>
        {icon}
        {children}
    </Row>
);

const FilterButton = ({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            height: 34,
            padding: '0 14px',
            borderRadius: 999,
            border: `1px solid ${active ? '#93C5FD' : '#E2E8F0'}`,
            backgroundColor: active ? '#EFF6FF' : '#FFFFFF',
            color: active ? '#1D4ED8' : '#475569',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
        }}
    >
        {label}
    </button>
);

const RangeButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            height: 28,
            padding: '0 10px',
            borderRadius: 7,
            border: '1px solid #E2E8F0',
            backgroundColor: '#FFFFFF',
            color: '#475569',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
        }}
    >
        {label}
    </button>
);

const NoticeLine = ({
    icon,
    text,
    tone,
}: {
    icon: React.ReactNode;
    text: string;
    tone: 'blue' | 'amber';
}) => {
    const palette = tone === 'blue'
        ? { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' }
        : { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A' };
    return (
        <Row
            className="items-center gap-[7px] rounded-[8px] border border-solid px-[10px] py-[7px]"
            style={{ backgroundColor: palette.bg, borderColor: palette.border, color: palette.color }}
        >
            {icon}
            <RegularText fontSize={12} lineHeight={17} color={palette.color}>{text}</RegularText>
        </Row>
    );
};

const HeaderCell = ({ text, width, flex }: { text: string; width?: number; flex?: boolean }) => (
    <span
        className={flex ? 'flex-1 font-noto-regular' : 'font-noto-regular'}
        style={{
            display: 'inline-block',
            width: width ?? undefined,
            fontSize: 12,
            lineHeight: '17px',
            color: '#64748B',
            letterSpacing: 0,
        }}
    >
        {text}
    </span>
);

const fieldInputStyle: React.CSSProperties = {
    width: '100%',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#0F172A',
    fontSize: 13,
    lineHeight: '18px',
};

const ghostButtonStyle: React.CSSProperties = {
    height: 36,
    padding: '0 14px',
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    color: '#334155',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
};

const cancelButtonStyle: React.CSSProperties = {
    height: 44,
    minWidth: 104,
    padding: '0 18px',
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    color: colors.LAVEL_2,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
};

const primaryButtonStyle: React.CSSProperties = {
    height: 44,
    minWidth: 154,
    padding: '0 20px',
    borderRadius: 8,
    border: 'none',
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: 700,
};

export default ModalGroupTicketSessionRegister;
